import { ReturnApprovalMode } from './policy';

export type ReturnCustomerLifecycleNotificationEvent =
  | 'RETURN_CREATED'
  | 'RETURN_APPROVED'
  | 'RETURN_DENIED'
  | 'RETURN_UPDATED'
  | 'LABEL_CREATED'
  | 'REMINDER_SENT'
  | 'RETURN_EXPIRED'
  | 'REFUND_PROCESSED'
  | 'STORE_CREDIT_ISSUED';

export type ReturnMerchantExceptionNotificationEvent =
  | 'HIGH_RISK_RETURN'
  | 'DAMAGED_RETURN'
  | 'AWAITING_APPROVAL'
  | 'DELIVERED_BACK'
  | 'PENDING_REFUND'
  | 'RETURN_EXPIRED';

export interface ReturnNotificationsSettingsV2 {
  version: 2;
  customerLifecycle: {
    enabled: boolean;
    events: ReturnCustomerLifecycleNotificationEvent[];
  };
  merchantExceptions: {
    enabled: boolean;
    events: ReturnMerchantExceptionNotificationEvent[];
  };
}

export enum ReturnStatus {
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  AWAITING_DROPOFF = 'AWAITING_DROPOFF',
  DENIED = 'DENIED',
  IN_TRANSIT = 'IN_TRANSIT',
  LABEL_READY = 'LABEL_READY',
  RECEIVED = 'RECEIVED',
  RESOLVED = 'RESOLVED',
  EXPIRED = 'EXPIRED',
}

export type ReturnBusinessStatus =
  | 'RETURN_FILED'
  | 'APPROVED_BY_MERCHANT'
  | 'DENIED_BY_MERCHANT'
  | 'AWAITING_CUSTOMER_SHIPMENT'
  | 'RETURN_IN_TRANSIT'
  | 'DELIVERED_PENDING_REVIEW'
  | 'ITEM_RECEIVED_BACK_AT_WAREHOUSE'
  | 'REFUND_PROCESSED'
  | 'STORE_CREDIT_PROCESSED'
  | 'RETURN_RESOLVED'
  | 'RETURN_EXPIRED'
  | 'UNKNOWN';

export type ReturnMetricBucket =
  | 'ALL'
  | 'NEEDS_REVIEW'
  | 'AWAITING_SHIPMENT'
  | 'IN_TRANSIT'
  | 'READY_TO_REFUND'
  | 'COMPLETE';

export type ReturnsDashboardBucket =
  | 'OPEN'
  | 'PENDING_REVIEW'
  | 'APPROVED'
  | 'IN_TRANSIT'
  | 'DELIVERED_BACK'
  | 'PENDING_REFUND'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'FLAGGED';

export type ReturnsDashboardOrderBy =
  | 'UPDATED_AT_DESC'
  | 'UPDATED_AT_ASC'
  | 'CREATED_AT_DESC'
  | 'CREATED_AT_ASC'
  | 'DEADLINE_ASC'
  | 'REFUND_DUE_ASC';

export interface ReturnsDashboardBucketCount {
  bucket: ReturnsDashboardBucket | string;
  count: number;
}

export interface ReturnMetricsAggregate {
  aggregate?: {
    count?: number | null;
  } | null;
}

export interface ReturnMetricsQueryResult {
  totalReturns?: ReturnMetricsAggregate | null;
  needsReview?: ReturnMetricsAggregate | null;
  awaitingShipment?: ReturnMetricsAggregate | null;
  inTransit?: ReturnMetricsAggregate | null;
  readyToRefund?: ReturnMetricsAggregate | null;
  complete?: ReturnMetricsAggregate | null;
}

export type ReturnMetricCounts = Record<ReturnMetricBucket, number>;

export interface ReturnAnalyticsOrderItem {
  id?: string | null;
  title?: string | null;
  sku?: string | null;
  price?: number | string | null;
  imageSrc?: string | null;
}

export interface ReturnAnalyticsReturnItem {
  id?: string | null;
  orderItemId?: string | null;
  quantity?: number | string | null;
  reason?: string | null;
  reasonComments?: string | null;
  requestedResolution?: string | null;
  resolution?: string | null;
  orderItem?: ReturnAnalyticsOrderItem | null;
}

export interface ReturnAnalyticsRecord {
  id: string;
  number?: string | null;
  status?: ReturnStatus | string | null;
  labelCost?: number | string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  order?: {
    total?: number | string | null;
    currencyCode?: string | null;
    paymentCurrencyCode?: string | null;
  } | null;
  returnItems?: ReturnAnalyticsReturnItem[] | null;
}

export interface ReturnsAnalyticsQueryResult {
  returns?: ReturnAnalyticsRecord[] | null;
}

export interface ReturnDashboardFlag {
  code: string;
  label: string;
  severity: string;
  source?: string | null;
  createdAt?: string | null;
  metadata?: Record<string, any> | null;
  message?: string | null;
}

export type ReturnNextAction =
  | 'REVIEW_RETURN'
  | 'CREATE_LABEL'
  | 'AWAITING_DROPOFF'
  | 'TRACK_RETURN'
  | 'INSPECT_RETURN'
  | 'CONFIRM_WAREHOUSE_RECEIPT'
  | 'REVIEW_INSPECTION'
  | 'ISSUE_REFUND'
  | 'ISSUE_STORE_CREDIT'
  | 'WAIT_FOR_REFUND_DUE'
  | 'REVIEW_SETTLEMENT'
  | 'AWAITING_RESOLUTION_RESPONSE'
  | 'REVIEW_DEFECT_RESOLUTION'
  | 'COMPLETE_EXTERNAL_RESOLUTION'
  | 'AWAITING_CUSTOMER_INFORMATION'
  | 'COMPLETE_RETURN_INSPECTION'
  | 'NONE'
  | string;

export interface ReturnEvent {
  id: string;
  type: string;
  source?: string | null;
  occurredAt: string;
  payload?: Record<string, any> | string | null;
}

export interface ReturnDashboardNode {
  id: string;
  number: string;
  status: ReturnStatus | string;
  orderId?: string | null;
  orderNumber?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  total?: number | string | null;
  currencyCode?: string | null;
  requestedResolution?: string | null;
  businessStatus?: ReturnBusinessStatus | string | null;
  businessStatusLabel?: string | null;
  nextAction?: string | null;
  reviewRequired?: boolean | null;
  riskLevel?: string | null;
  reviewReasons?: string[] | null;
  ruleFlags?: ReturnDashboardFlag[] | null;
  trackingStatus?: string | null;
  trackingNumber?: string | null;
  trackingCarrier?: string | null;
  trackingService?: string | null;
  deadlineAt?: string | null;
  refundDueAt?: string | null;
  updatedAt?: string | null;
}

export interface SelectedReturnShippingOption {
  source?: 'SHIPAID_RATES' | 'MERCHANT_CARRIER_ACCOUNT' | string;
  courierServiceId?: string;
  carrier?: string | null;
  service?: string | null;
  displayName?: string | null;
  customerVisible?: boolean;
  sortOrder?: number;
  labelCost?: number | null;
  labelSource?: string | null;
  [key: string]: any;
}

export interface ReturnDynamicFee {
  label: string;
  type: 'PERCENTAGE' | 'FLAT' | 'FIXED';
  value: number;
  calculatedAmount?: number | null;
}

export interface Return {
  id: string;
  number: string;
  orderId: string;
  status: ReturnStatus;
  requestedResolution?: string | null;
  businessStatus?: ReturnBusinessStatus | string | null;
  businessStatusLabel?: string | null;
  nextAction?: ReturnNextAction | null;
  reviewRequired?: boolean | null;
  riskLevel?: string | null;
  reviewReasons?: string[] | null;
  ruleFlags?: ReturnDashboardFlag[] | null;
  deadlineAt?: string | null;
  customerNotes?: string;
  images?: string[] | null;
  videos?: string[] | null;
  denialReason?: string;
  denialDescription?: string;
  shippingId?: string;
  labelUrl?: string;
  carrier?: string | null;
  service?: string | null;
  labelCost?: number | null;
  labelSource?: string | null;
  selectedShippingOption?: SelectedReturnShippingOption | null;
  trackingNumber?: string;
  shopifyReturnId?: string | null;
  shopifyReturnName?: string | null;
  shopifyReturnStatus?: string | null;
  shopifyReverseFulfillmentOrderIds?: string[] | null;
  shopifyReturnSyncedAt?: string | null;
  shopifyReturnSyncStatus?: string | null;
  shopifyReturnSyncError?: string | null;
  shopifyReturnLastWebhookAt?: string | null;
  eligibleAt?: string | null;
  eligibleUntil?: string | null;
  approvedAt?: string | null;
  labelCreatedAt?: string | null;
  shipByAt?: string | null;
  expiredAt?: string | null;
  firstCarrierScanAt?: string | null;
  inboundDeliveredAt?: string | null;
  receivedAt?: string | null;
  refundDueAt?: string | null;
  refundedAt?: string | null;
  storeCreditIssuedAt?: string | null;
  settlementStatus?: string | null;
  settlementType?: string | null;
  settlementTriggeredAt?: string | null;
  settlementCompletedAt?: string | null;
  settlementFailureReason?: string | null;
  trackingStatus?: string | null;
  trackingUpdatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  storeId: string;
  deflectionOption?: string;
  meta?: Record<string, any> | null;
  policySnapshot?: {
    dynamicFees?: ReturnDynamicFee[] | null;
    [key: string]: unknown;
  } | null;
  order: {
    id?: string;
    orderId?: string;
    number: string;
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    shippingAddress?: Record<string, unknown> | null;
    total: number;
    createdAt: string;
    subtotal: number;
    shippingTotal: number;
    tax: number;
    paymentCurrencyCode: string;
    currencyCode?: string | null;
  };
  returnItems: ReturnItem[];
  policy?: {
    eligible: boolean;
    approvalMode: ReturnApprovalMode | string;
    dynamicFees: ReturnDynamicFee[];
    deflectionOffers: Array<{
      id?: string | null;
      type: string;
      offerType?: string | null;
      label: string;
      value: number;
      valueType: string;
    }>;
  };
  activities?: Array<{
    eventType: string;
    createdAt: string;
  }>;
  events?: ReturnEvent[];
}

export interface ReturnItem {
  id: string;
  orderItemId: string;
  quantity: number;
  reason?: string;
  reasonComments?: string | null;
  requestedResolution?: string | null;
  resolution?: string | null;
  orderItem: {
    id?: string;
    title: string;
    sku: string;
    price: number;
    total?: number | null;
    quantity?: number | null;
    imageSrc?: string;
  };
}
